import React, { useRef, useState, useEffect } from "react";
import { Share2, Download, Link as LinkIcon, ImageDown, Film, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const truncate = (s, n) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s || "");

// A few bold seasonal skins for the Dare Card so shares feel fresh and on-brand.
const THEMES = {
  ember: { name: "Ember", stops: ["#1a1030", "#3a1630", "#f5b301"], accent: "#ffd166" },
  frost: { name: "Frost", stops: ["#06121f", "#0b2a4a", "#7fd4ff"], accent: "#e6f7ff" },
  neon:  { name: "Neon",  stops: ["#0a0018", "#2a0a4a", "#ff2fb0"], accent: "#8afff0" },
  bloom: { name: "Bloom", stops: ["#12210f", "#1f3a16", "#7be04a"], accent: "#eaffcc" },
};

const canRecord = typeof document !== "undefined"
  && typeof window !== "undefined" && !!window.MediaRecorder
  && !!HTMLCanvasElement.prototype.captureStream;

// Shareable "dare card": challenger + Wave score + top taunt. Animates in the dialog and can be
// recorded as a short looping WebM clip for punchier social shares.
export default function DareCard({ dare, topTaunt, event }) {
  const canvasRef = useRef(null);
  const sparksRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [animate, setAnimate] = useState(true);
  const [recording, setRecording] = useState(false);

  const seasonal = event?.accent
    ? { name: event.name || "Seasonal", stops: ["#0e0b16", "#1c1330", event.accent], accent: event.accent }
    : null;
  const allThemes = seasonal ? { seasonal, ...THEMES } : THEMES;
  const themeKeys = Object.keys(allThemes);
  const [theme, setTheme] = useState(seasonal ? "seasonal" : "ember");
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (event?.accent && !touched) setTheme("seasonal"); }, [event?.accent, touched]);

  const shareUrl = `${window.location.origin}/app/arcade/${dare?.game_id}?challenge=${dare?.code}`;
  const shortUrl = shareUrl.replace(/^https?:\/\//, "");

  if (!sparksRef.current) {
    sparksRef.current = Array.from({ length: 14 }, () => ({
      x: Math.random(), speed: 40 + Math.random() * 90, phase: Math.random(), size: 1.5 + Math.random() * 3,
    }));
  }

  // Animation + render loop (drives both the live preview and the recorded clip).
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = 1200, H = 630;
    const th = allThemes[theme] || THEMES.ember;
    const sparks = sparksRef.current;
    let raf; const start = performance.now();

    const draw = (elapsedMs) => {
      const t = animate ? elapsedMs / 1000 : 0;

      const grad = ctx.createLinearGradient(0, 0, W, H);
      grad.addColorStop(0, th.stops[0]); grad.addColorStop(0.55, th.stops[1]); grad.addColorStop(1, th.stops[2]);
      ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fillRect(0, 0, W, H);

      if (animate) {
        for (const s of sparks) {
          const y = H - (((t * s.speed) + s.phase * (H + 60)) % (H + 60));
          ctx.globalAlpha = 0.35;
          ctx.fillStyle = th.accent;
          ctx.beginPath(); ctx.arc(s.x * W, y, s.size, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;
        const sx = ((t * 240) % (W + 480)) - 240;
        const sheen = ctx.createLinearGradient(sx, 0, sx + 260, H);
        sheen.addColorStop(0, "rgba(255,255,255,0)");
        sheen.addColorStop(0.5, "rgba(255,255,255,0.06)");
        sheen.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = sheen; ctx.fillRect(0, 0, W, H);
      }

      ctx.fillStyle = "rgba(255,255,255,0.85)"; ctx.textBaseline = "top";
      ctx.font = "bold 32px Inter, system-ui, sans-serif";
      ctx.fillText("🔥 GAMEVIEW · DARE", 60, 50);

      ctx.fillStyle = "rgba(255,255,255,0.82)"; ctx.textBaseline = "alphabetic";
      ctx.font = "36px Inter, system-ui, sans-serif";
      ctx.fillText(truncate(`${dare?.challenger_name || "A challenger"} dares you to beat`, 42), 60, 210);

      ctx.save();
      if (animate) { ctx.shadowColor = th.accent; ctx.shadowBlur = 18 + 14 * Math.abs(Math.sin(t * 2.2)); }
      ctx.fillStyle = th.accent; ctx.font = "bold 150px Inter, system-ui, sans-serif";
      ctx.fillText(`WAVE ${dare?.wave ?? 0}`, 56, 340);
      ctx.restore();

      ctx.fillStyle = "rgba(255,255,255,0.92)"; ctx.font = "bold 40px Inter, system-ui, sans-serif";
      ctx.fillText(truncate(dare?.game_title || "this game", 34), 60, 400);

      if (topTaunt?.text) {
        ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "italic 30px Inter, system-ui, sans-serif";
        ctx.fillText(`“${truncate(topTaunt.text, 52)}”`, 60, 470);
        ctx.fillStyle = "rgba(255,255,255,0.6)"; ctx.font = "24px Inter, system-ui, sans-serif";
        ctx.fillText(`— ${truncate(topTaunt.user_name || "a player", 26)}`, 60, 508);
      }

      ctx.fillStyle = "rgba(255,255,255,0.78)"; ctx.font = "27px Inter, system-ui, sans-serif";
      ctx.fillText(`▶ Take the dare — ${truncate(shortUrl, 44)}`, 60, 578);
    };
    draw(0);                                     // paint one frame synchronously so it's never blank
    const loop = (now) => { draw(now - start); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, animate, theme, event?.accent, dare, topTaunt, shortUrl]);

  const toBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await toBlob(); if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `dare-wave-${dare?.wave ?? 0}.png`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Dare card downloaded!");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Dare link copied — paste it anywhere!"); }
    catch { toast.error("Couldn't copy the link."); }
  };

  const nativeShare = async () => {
    const text = `${dare?.challenger_name || "Someone"} dares you to beat Wave ${dare?.wave ?? 0} on "${dare?.game_title || "GameView"}"! Can you top it?`;
    try {
      const blob = await toBlob();
      const file = blob ? new File([blob], "gameview-dare.png", { type: "image/png" }) : null;
      if (file && typeof navigator.canShare === "function" && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "GameView Dare", text, url: shareUrl });
      } else {
        await navigator.share({ title: "GameView Dare", text, url: shareUrl });
      }
    } catch { /* cancelled or unsupported */ }
  };

  const recordClip = () => {
    if (!canRecord || recording) return;
    if (!animate) setAnimate(true);
    setRecording(true);
    try {
      const stream = canvasRef.current.captureStream(30);
      let mime = "video/webm;codecs=vp9";
      if (!MediaRecorder.isTypeSupported(mime)) mime = "video/webm;codecs=vp8";
      if (!MediaRecorder.isTypeSupported(mime)) mime = "video/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4000000 });
      const chunks = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `dare-wave-${dare?.wave ?? 0}.webm`;
        a.click(); URL.revokeObjectURL(url);
        setRecording(false);
        toast.success("Dare clip saved — share it anywhere!");
      };
      rec.start();
      setTimeout(() => { try { rec.stop(); } catch { setRecording(false); } }, 4000);
    } catch {
      setRecording(false);
      toast.error("Clip recording isn't supported on this browser.");
    }
  };

  if (!dare?.code) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-7 px-3 text-xs" data-testid="gp-dare-card-btn">
          <ImageDown className="w-3.5 h-3.5" /> Dare Card
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="gp-dare-card-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#f5b301]" /> Share this dare</DialogTitle></DialogHeader>
        <canvas ref={canvasRef} width={1200} height={630} className="w-full rounded-xl border border-[hsl(var(--border))]" data-testid="gp-dare-card" />
        <div className="flex items-center gap-2 flex-wrap" data-testid="gp-dare-card-themes">
          <span className="text-xs text-[hsl(var(--muted-foreground))]">Skin:</span>
          {themeKeys.map((k) => (
            <button
              key={k}
              onClick={() => { setTouched(true); setTheme(k); }}
              data-testid={`gp-dare-card-theme-${k}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${theme === k ? "border-[#f5b301] bg-[#f5b301]/15 text-[#f5b301]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
            >
              {allThemes[k].name}
            </button>
          ))}
          <button
            onClick={() => setAnimate((a) => !a)}
            data-testid="gp-dare-card-animate-toggle"
            className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1 ${animate ? "border-[#f5b301] bg-[#f5b301]/15 text-[#f5b301]" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
          >
            <Sparkles className="w-3.5 h-3.5" /> {animate ? "Animated" : "Static"}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} className="rounded-full gap-1.5 flex-1" data-testid="gp-dare-card-download"><Download className="w-4 h-4" /> Image</Button>
          {canRecord && (
            <Button variant="outline" onClick={recordClip} disabled={recording} className="rounded-full gap-1.5 flex-1" data-testid="gp-dare-card-record">
              <Film className="w-4 h-4" /> {recording ? "Recording…" : "Clip (4s)"}
            </Button>
          )}
          <Button variant="outline" onClick={copyLink} className="rounded-full gap-1.5 flex-1" data-testid="gp-dare-card-copy"><LinkIcon className="w-4 h-4" /> Copy link</Button>
          {typeof navigator !== "undefined" && navigator.share && (
            <Button variant="outline" onClick={nativeShare} className="rounded-full gap-1.5 flex-1" data-testid="gp-dare-card-share"><Share2 className="w-4 h-4" /> Share…</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
